import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Sparkles, Loader2, ChevronRight, ChevronLeft, Check, Settings, FileText, Eye, Download } from 'lucide-react';
import { useAuth } from '@/context/auth';
import { useRestaurantImages } from '@/hooks/useRestaurantMedia';
import { ImageModeSelector, ImageMode } from '@/components/media/images/ImageModeSelector';
import { ImageUseCaseSelector, ImageUseCase } from '@/components/media/images/ImageUseCaseSelector';
import { ImageConfigForm, ImageConfig } from '@/components/media/images/ImageConfigForm';
import { AspectRatioPreview } from '@/components/media/images/AspectRatioPreview';
import { PromptInput } from '@/components/media/shared/PromptInput';
import { MediaSelector } from '@/components/media/shared/MediaSelector';
import { toast } from '@/hooks/use-toast';

type ImageGenerationStep = 'config' | 'prompt' | 'preview';

interface FormStep {
  id: ImageGenerationStep;
  label: string;
  icon: React.ReactNode;
}

const FORM_STEPS: FormStep[] = [
  { id: 'config', label: 'Configuration', icon: <Settings className="h-4 w-4" /> },
  { id: 'prompt', label: 'Prompt', icon: <FileText className="h-4 w-4" /> },
  { id: 'preview', label: 'Preview', icon: <Eye className="h-4 w-4" /> }
];

/**
 * ImagesGenerateTab - AI image generation interface with multi-step workflow
 *
 * Step 1 (Configuration):
 * - Generation Mode (Text-to-Image | Reference Images)
 * - Use Case (Static Image | Video Thumbnail)
 * - Aspect Ratio (conditional on use case)
 * - Preview of selected aspect ratio
 *
 * Step 2 (Prompt):
 * - Display selected configuration
 * - Image prompt input
 * - Reference image selector (when Reference Images mode selected)
 * - Generate button
 *
 * Following Pumpd's TriggerForm multi-step pattern
 */
export const ImagesGenerateTab: React.FC = () => {
  const { user } = useAuth();
  const { images, generateImageAsync, isGenerating } = useRestaurantImages(user!.organisationId);

  // Step navigation
  const [currentStep, setCurrentStep] = useState<ImageGenerationStep>('config');
  const [completedSteps, setCompletedSteps] = useState<Set<ImageGenerationStep>>(new Set());

  // Form state
  const [mode, setMode] = useState<ImageMode>('text-to-image');
  const [useCase, setUseCase] = useState<ImageUseCase>('static-image');
  const [config, setConfig] = useState<ImageConfig>({ aspectRatio: '4:5' }); // Default to recommended
  const [prompt, setPrompt] = useState('');
  const [selectedImageIds, setSelectedImageIds] = useState<string[]>([]); // For reference images mode

  // Generation result
  const [generatedImageUrl, setGeneratedImageUrl] = useState<string | null>(null);
  const [generationSuccess, setGenerationSuccess] = useState(false);
  const [filename, setFilename] = useState(''); // Image filename
  const [tags, setTags] = useState<string>(''); // Comma-separated tags

  // Auto-select 9:16 when Video Thumbnail is chosen, reset to 4:5 when switching to Static Image
  useEffect(() => {
    if (useCase === 'video-thumbnail' && config.aspectRatio !== '9:16') {
      setConfig({ aspectRatio: '9:16' });
    } else if (useCase === 'static-image' && config.aspectRatio === '9:16') {
      // Reset to recommended when switching from video thumbnail to static image
      setConfig({ aspectRatio: '4:5' });
    }
  }, [useCase]);

  // Step validation
  const validateStep = (step: ImageGenerationStep): boolean => {
    if (step === 'config') {
      // All config fields are required and have defaults, so always valid
      return true;
    }

    if (step === 'prompt') {
      if (!prompt.trim()) {
        toast({
          title: 'Validation error',
          description: 'Please enter a prompt to generate an image',
          variant: 'destructive'
        });
        return false;
      }

      // Validate reference images if in reference mode
      if (mode === 'reference-images' && selectedImageIds.length === 0) {
        toast({
          title: 'Validation error',
          description: 'Please select at least 1 reference image',
          variant: 'destructive'
        });
        return false;
      }
    }

    return true;
  };

  // Step navigation handlers
  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCompletedSteps(prev => new Set([...prev, currentStep]));

      const currentIndex = FORM_STEPS.findIndex(s => s.id === currentStep);
      if (currentIndex < FORM_STEPS.length - 1) {
        setCurrentStep(FORM_STEPS[currentIndex + 1].id);
      }
    }
  };

  const handlePrevious = () => {
    const currentIndex = FORM_STEPS.findIndex(s => s.id === currentStep);
    if (currentIndex > 0) {
      setCurrentStep(FORM_STEPS[currentIndex - 1].id);
    }
  };

  const handleStepClick = (stepId: ImageGenerationStep) => {
    const stepIndex = FORM_STEPS.findIndex(s => s.id === stepId);
    const currentIndex = FORM_STEPS.findIndex(s => s.id === currentStep);

    // Can click on current step or any completed step
    if (stepId === currentStep || completedSteps.has(stepId)) {
      setCurrentStep(stepId);
      return;
    }

    // Can click on the next step if all previous steps are completed
    if (stepIndex === currentIndex + 1) {
      const allPreviousCompleted = FORM_STEPS
        .slice(0, stepIndex)
        .every(step => completedSteps.has(step.id));

      if (allPreviousCompleted) {
        handleNext();
      }
    }
  };

  const handleGenerate = async () => {
    if (!validateStep('prompt')) {
      return;
    }

    if (!user?.organisationId) {
      toast({
        title: 'Error',
        description: 'User organization not found',
        variant: 'destructive'
      });
      return;
    }

    try {
      const requestData: any = {
        mode,
        prompt: prompt.trim(),
        imageConfig: config,
        organisation_id: user.organisationId
      };

      // Add reference sources if in reference-images mode
      if (mode === 'reference-images' && selectedImageIds.length > 0) {
        requestData.referenceSources = selectedImageIds.map(imageId => {
          // Find the image to get its source_type
          const image = images.find(img => img.id === imageId);
          if (!image) {
            throw new Error(`Image ${imageId} not found in library`);
          }

          // Map source_type to backend's expected sourceType format
          // 'ai_generated' -> 'ai', 'uploaded' -> 'uploaded'
          const sourceType = image.source_type === 'ai_generated' ? 'ai' : 'uploaded';

          return {
            id: imageId,
            sourceType: sourceType
          };
        });
      }

      // Add filename if provided
      if (filename.trim()) {
        requestData.filename = filename.trim();
      }

      // Parse and add tags if provided
      if (tags.trim()) {
        requestData.tags = tags.split(',').map(tag => tag.trim()).filter(tag => tag.length > 0);
      }

      const result = await generateImageAsync(requestData);

      // Success - set the generated image and mark success (but keep form data)
      if (result && result.public_url) {
        setGeneratedImageUrl(result.public_url);
        setGenerationSuccess(true);

        toast({
          title: 'Image generated successfully!',
          description: mode === 'reference-images'
            ? `Your composed image using ${selectedImageIds.length} reference${selectedImageIds.length > 1 ? 's' : ''} is ready`
            : 'Your AI-generated image is ready'
        });
      }
    } catch (error: any) {
      console.error('Image generation failed:', error);
      setGenerationSuccess(false);
      setGeneratedImageUrl(null);
    }
  };

  const getStepStatus = (stepId: ImageGenerationStep): 'completed' | 'current' | 'upcoming' => {
    if (completedSteps.has(stepId)) return 'completed';
    if (stepId === currentStep) return 'current';
    return 'upcoming';
  };

  const isStepClickable = (stepId: ImageGenerationStep): boolean => {
    if (stepId === currentStep) return true;
    if (completedSteps.has(stepId)) return true;

    const stepIndex = FORM_STEPS.findIndex(s => s.id === stepId);
    if (stepIndex > 0) {
      return FORM_STEPS.slice(0, stepIndex).every(step => completedSteps.has(step.id));
    }

    return false;
  };

  const handleStartNew = () => {
    // Reset everything for a new generation
    setPrompt('');
    setSelectedImageIds([]);
    setCurrentStep('config');
    setCompletedSteps(new Set());
    setGeneratedImageUrl(null);
    setGenerationSuccess(false);
  };

  return (
    <div className="space-y-6">
      {/* Step Progress Indicator */}
      <Card>
        <CardContent className="p-4 sm:p-6">
          <div className="flex items-center justify-center gap-2 sm:gap-3 overflow-x-auto">
            {FORM_STEPS.map((step, index) => {
              const status = getStepStatus(step.id);
              const clickable = isStepClickable(step.id);

              return (
                <div key={step.id} className="flex items-center">
                  {/* Step Button */}
                  <button
                    onClick={() => clickable && handleStepClick(step.id)}
                    disabled={!clickable}
                    className={`
                      flex items-center gap-2 px-4 sm:px-6 py-2.5 rounded-lg transition-all font-medium
                      ${status === 'completed' ? 'bg-green-600 hover:bg-green-700' : ''}
                      ${status === 'current' ? 'bg-primary hover:bg-primary/90' : ''}
                      ${status === 'upcoming' ? 'bg-muted' : ''}
                      ${clickable ? 'cursor-pointer' : 'cursor-not-allowed opacity-60'}
                    `}
                  >
                    <div className="flex items-center gap-2">
                      {status === 'completed' ? (
                        <Check className="h-4 w-4 text-white stroke-white" />
                      ) : (
                        <span className={`${status === 'current' ? 'text-white [&_svg]:stroke-white' : 'text-muted-foreground'}`}>
                          {step.icon}
                        </span>
                      )}
                      <span className={`text-sm whitespace-nowrap hidden sm:inline ${
                        status === 'completed' || status === 'current' ? 'text-white' : 'text-muted-foreground'
                      }`}>
                        {step.label}
                      </span>
                    </div>
                  </button>

                  {/* Connector Line */}
                  {index < FORM_STEPS.length - 1 && (
                    <div
                      className={`w-8 sm:w-16 h-0.5 ${
                        completedSteps.has(FORM_STEPS[index + 1].id)
                          ? 'bg-green-600'
                          : 'bg-border'
                      }`}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Step 1: Configuration */}
      {currentStep === 'config' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Sparkles className="h-5 w-5 text-primary" />
                  <CardTitle>Configure Your Image</CardTitle>
                </div>
                <CardDescription>
                  Choose how you want to generate your image
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <ImageModeSelector value={mode} onChange={setMode} />
                <Separator />
                <ImageUseCaseSelector value={useCase} onChange={setUseCase} />
                <Separator />
                <ImageConfigForm config={config} onChange={setConfig} useCase={useCase} />

                <div className="flex justify-end pt-4">
                  <Button onClick={handleNext} size="lg">
                    Next: Enter Prompt
                    <ChevronRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Preview Column */}
          <div className="space-y-6">
            <AspectRatioPreview aspectRatio={config.aspectRatio} />
          </div>
        </div>
      )}

      {/* Step 2: Prompt & Generation */}
      {currentStep === 'prompt' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            {/* Configuration Summary */}
            <Card className="bg-muted/50">
              <CardContent className="p-4">
                <div className="flex flex-wrap gap-2 items-center">
                  <span className="text-sm font-medium">Selected Configuration:</span>
                  <Badge variant="outline">{mode === 'text-to-image' ? 'Text-to-Image' : 'Reference Images'}</Badge>
                  <Badge variant="outline">{useCase === 'static-image' ? 'Static Image' : 'Video Thumbnail'}</Badge>
                  <Badge variant="secondary">{config.aspectRatio}</Badge>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setCurrentStep('config')}
                    className="ml-auto"
                  >
                    Change Configuration
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>
                  {mode === 'reference-images' ? 'Select Image(s) and Enter Your Prompt' : 'Enter Your Prompt'}
                </CardTitle>
                <CardDescription>
                  {mode === 'reference-images'
                    ? 'Choose reference images and describe how to compose them'
                    : 'Describe the image you want to generate'
                  }
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {mode === 'text-to-image' ? (
                  <>
                    <PromptInput
                      label="Image Prompt"
                      value={prompt}
                      onChange={setPrompt}
                      placeholder="A gourmet burger with melted cheese, fresh lettuce, and caramelized onions on a rustic wooden board, professional food photography, warm lighting, shallow depth of field"
                      helperText="Describe the image you want to generate. Be specific about composition, lighting, and style."
                      required
                      rows={8}
                      maxLength={5000}
                      disabled={isGenerating}
                    />

                    <Separator />

                    {/* Filename and Tags Inputs */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="filename">
                          Image Filename <span className="text-muted-foreground">(optional)</span>
                        </Label>
                        <Input
                          id="filename"
                          value={filename}
                          onChange={(e) => setFilename(e.target.value)}
                          placeholder="summer-burger-special"
                          disabled={isGenerating}
                        />
                        <p className="text-xs text-muted-foreground">
                          A descriptive name for your image (e.g., "burger-promo-2025")
                        </p>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="tags">
                          Tags <span className="text-muted-foreground">(optional)</span>
                        </Label>
                        <Input
                          id="tags"
                          value={tags}
                          onChange={(e) => setTags(e.target.value)}
                          placeholder="burger, promo, summer"
                          disabled={isGenerating}
                        />
                        <p className="text-xs text-muted-foreground">
                          Comma-separated tags for organizing your images
                        </p>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="space-y-6">
                    {/* Reference Images Selector */}
                    <MediaSelector
                      value={selectedImageIds}
                      onChange={setSelectedImageIds}
                      min={1}
                      max={10}
                      showRefresh={true}
                    />

                    {/* Composition Prompt */}
                    <PromptInput
                      label="Composition Prompt"
                      value={prompt}
                      onChange={setPrompt}
                      placeholder="Blend these images together to create a vibrant collage showing our best dishes. Emphasize colors and textures, professional food photography style."
                      helperText="Describe how you want the reference images to be composed together."
                      required
                      rows={6}
                      maxLength={500}
                      disabled={isGenerating}
                    />

                    <Separator />

                    {/* Filename and Tags Inputs */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="filename-ref">
                          Image Filename <span className="text-muted-foreground">(optional)</span>
                        </Label>
                        <Input
                          id="filename-ref"
                          value={filename}
                          onChange={(e) => setFilename(e.target.value)}
                          placeholder="menu-collage-2025"
                          disabled={isGenerating}
                        />
                        <p className="text-xs text-muted-foreground">
                          A descriptive name for your composed image
                        </p>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="tags-ref">
                          Tags <span className="text-muted-foreground">(optional)</span>
                        </Label>
                        <Input
                          id="tags-ref"
                          value={tags}
                          onChange={(e) => setTags(e.target.value)}
                          placeholder="collage, menu, featured"
                          disabled={isGenerating}
                        />
                        <p className="text-xs text-muted-foreground">
                          Comma-separated tags for organizing your images
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                <div className="flex items-center justify-between pt-4">
                  <Button variant="outline" onClick={handlePrevious}>
                    <ChevronLeft className="mr-2 h-4 w-4" />
                    Previous
                  </Button>

                  <Button
                    onClick={handleNext}
                    disabled={!prompt.trim() || (mode === 'reference-images' && selectedImageIds.length === 0)}
                    size="lg"
                  >
                    Preview & Generate
                    <ChevronRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Tips Column */}
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Tips for Better Results</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <h4 className="font-semibold text-sm mb-1">Example Prompts:</h4>
                  <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
                    <li>Professional food photography of fresh pasta</li>
                    <li>Cozy restaurant interior with warm lighting</li>
                    <li>Colorful smoothie bowl with fresh berries</li>
                  </ul>
                </div>

                <div>
                  <h4 className="font-semibold text-sm mb-1">For Best Prompts:</h4>
                  <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
                    <li>Be specific about composition</li>
                    <li>Mention photography style</li>
                    <li>Describe lighting</li>
                    <li>Include color preferences</li>
                  </ul>
                </div>

                <div>
                  <h4 className="font-semibold text-sm mb-1">Generation Info:</h4>
                  <p className="text-sm text-muted-foreground">
                    • Google Gemini 2.5 Flash<br />
                    • Time: 10-30 seconds<br />
                    • Cost: ~$0.04 per image
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* Step 3: Preview & Generate */}
      {currentStep === 'preview' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            {/* Configuration Summary */}
            <Card>
              <CardHeader>
                <CardTitle>
                  {generationSuccess ? 'Your Generated Image' : 'Review Your Configuration'}
                </CardTitle>
                <CardDescription>
                  {generationSuccess
                    ? 'Image generated successfully - make adjustments or start a new generation'
                    : 'Check everything before generating your image'
                  }
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Show generated image if available */}
                {generatedImageUrl && (
                  <div className="space-y-4">
                    <div className="rounded-lg overflow-hidden border bg-muted/50">
                      <img
                        src={generatedImageUrl}
                        alt="Generated image"
                        className="w-full h-auto"
                      />
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        onClick={() => window.open(generatedImageUrl, '_blank')}
                        className="flex-1"
                      >
                        <Eye className="mr-2 h-4 w-4" />
                        Open Full Size
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => {
                          const link = document.createElement('a');
                          link.href = generatedImageUrl;
                          link.download = `generated-${Date.now()}.png`;
                          link.click();
                        }}
                        className="flex-1"
                      >
                        <Download className="mr-2 h-4 w-4" />
                        Download
                      </Button>
                    </div>
                    <Separator />
                  </div>
                )}

                {/* Configuration details */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs text-muted-foreground">Generation Mode</Label>
                    <p className="font-medium">{mode === 'text-to-image' ? 'Text-to-Image' : 'Reference Images'}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Use Case</Label>
                    <p className="font-medium">{useCase === 'static-image' ? 'Static Image' : 'Video Thumbnail'}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Aspect Ratio</Label>
                    <p className="font-medium">{config.aspectRatio}</p>
                  </div>
                  {mode === 'reference-images' && (
                    <div>
                      <Label className="text-xs text-muted-foreground">Reference Images</Label>
                      <p className="font-medium">{selectedImageIds.length} selected</p>
                    </div>
                  )}
                </div>

                <Separator />

                <div>
                  <Label className="text-xs text-muted-foreground">Your Prompt</Label>
                  <Card className="mt-2 bg-muted/50">
                    <CardContent className="p-4">
                      <p className="text-sm">{prompt}</p>
                    </CardContent>
                  </Card>
                  <div className="flex gap-2 mt-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setCurrentStep('config')}
                    >
                      Edit Configuration
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setCurrentStep('prompt')}
                    >
                      Edit Prompt
                    </Button>
                  </div>
                </div>

                {/* Action buttons */}
                <div className="flex items-center justify-between pt-4">
                  {!generationSuccess ? (
                    <>
                      <Button variant="outline" onClick={handlePrevious}>
                        <ChevronLeft className="mr-2 h-4 w-4" />
                        Previous
                      </Button>

                      <Button
                        onClick={handleGenerate}
                        disabled={isGenerating}
                        size="lg"
                      >
                        {isGenerating ? (
                          <>
                            <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                            Generating Image...
                          </>
                        ) : (
                          <>
                            <Sparkles className="mr-2 h-5 w-5" />
                            Generate Image
                          </>
                        )}
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button variant="outline" onClick={handleStartNew}>
                        Start New Generation
                      </Button>
                      <Button
                        onClick={handleGenerate}
                        disabled={isGenerating}
                        size="lg"
                      >
                        {isGenerating ? (
                          <>
                            <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                            Generating...
                          </>
                        ) : (
                          <>
                            <Sparkles className="mr-2 h-5 w-5" />
                            Generate Again
                          </>
                        )}
                      </Button>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Info Column */}
          <div className="space-y-6">
            {!generatedImageUrl && (
              <AspectRatioPreview aspectRatio={config.aspectRatio} />
            )}

            <Card>
              <CardHeader>
                <CardTitle className="text-base">
                  {generationSuccess ? 'What Now?' : 'What Happens Next?'}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {generationSuccess ? (
                  <>
                    <p className="text-sm text-muted-foreground">
                      Your image has been saved to the Library tab. You can:
                    </p>
                    <ul className="text-sm text-muted-foreground space-y-2 list-disc list-inside">
                      <li>Download it using the button above</li>
                      <li>Find it in the Library tab for use in campaigns</li>
                      <li>Edit your configuration and generate again</li>
                      <li>Start a completely new generation</li>
                    </ul>
                    <div className="pt-2">
                      <p className="text-xs text-muted-foreground">
                        💡 <strong>Tip:</strong> If you want to refine the result, click "Edit Prompt" or "Edit Configuration" above to make adjustments and generate again.
                      </p>
                    </div>
                  </>
                ) : (
                  <>
                    <p className="text-sm text-muted-foreground">
                      When you click "Generate Image":
                    </p>
                    <ul className="text-sm text-muted-foreground space-y-2 list-disc list-inside">
                      <li>Your image will be generated using Google Gemini AI</li>
                      <li>This typically takes 10-30 seconds</li>
                      <li>The preview will appear here automatically</li>
                      <li>You can then download or generate again</li>
                    </ul>
                    <div className="pt-2">
                      <p className="text-xs text-muted-foreground">
                        💡 <strong>Tip:</strong> After generation, you can make adjustments and regenerate without starting over.
                      </p>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImagesGenerateTab;
